import { ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { v4 as uuidv4 } from 'uuid';
import { useAuthStore } from '@/stores/auth';
import { useEncounterFlow } from '@/common/workflowEngine/useEncounterFlow';
import { useWorkflow } from '@/common/workflowEngine/useWorkflow';
import { useProfileFlow } from '@/common/workflowEngine/useProfileFlow';
import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { getUrlFromCanonical } from '@/common/core';
import { Encounter, Patient, BN_Relationship } from '@/fhirworks';
import { crud } from '@/common/crud';

export function usePortalApplication() {
    const router = useRouter();
    const route = useRoute();
    const authStore = useAuthStore();
    const { postTransaction } = useWorkflow();
    const isLoading = ref(false);

    /**
     * Start a new application
     * @param {string} applicationType - 'self' or 'other'
     * Creates patient if needed, establishes relationship, creates encounter, and opens form filler
     */
    const startApplication = async (applicationType) => {
        isLoading.value = true;

        let patient = authStore.patient;

        try {
            const portalPacketId = authStore.organizationAccount?.portalPacketId;
            if (!portalPacketId) {
                console.error('No portal packet configured for this organization');
                isLoading.value = false;
                return;
            }

            if (applicationType === 'other') {
                // Create a new patient resource for someone else
                const patientId = uuidv4();
                const mrn = uuidv4().substring(24); // Last 8 characters of UUID for MRN

                const newPatient = new Patient({
                    id: patientId,
                    active: true,
                    inquiryStatus: 'pending',
                    gender: 'unknown',
                    name: [
                        {
                            use: 'official',
                        },
                    ],
                    identifier: [
                        {
                            type: {
                                coding: [
                                    {
                                        system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
                                        code: 'MR',
                                        display: 'Medical record number',
                                    },
                                ],
                            },
                            system: 'https://' + authStore.account.fhirApiUri + '/patients',
                            value: mrn,
                        },
                    ],
                    managingOrganization: {
                        id: authStore.organizationAccount.id,
                        resourceType: 'Organization',
                    },
                });

                const relationship = new BN_Relationship({
                    subject: {
                        id: authStore.patient.id,
                        resourceType: 'Patient',
                    },
                    related: {
                        id: patientId,
                        resourceType: 'Patient',
                    },
                    active: true,
                    period: {
                        start: new Date().toISOString(),
                    },
                });
                const inverseRelationship = new BN_Relationship({
                    subject: {
                        id: patientId,
                        resourceType: 'Patient',
                    },
                    related: {
                        id: authStore.patient.id,
                        resourceType: 'Patient',
                    },
                    portalAccess: true,
                    active: true,
                    period: {
                        start: new Date().toISOString(),
                    },
                });

                // Create the patient first
                const patientTransaction = [crud.post(newPatient), crud.post(relationship), crud.post(inverseRelationship)];
                const patientResponse = await postTransaction(patientTransaction);
                patient = patientResponse?.find((item) => item.resourceType === 'Patient');

                if (!patient) {
                    console.error('Failed to create patient');
                    isLoading.value = false;
                    return;
                }

                // Refresh related persons to include the new patient
                await authStore.refreshRelatedPersons();

                // Set the new patient as the viewing patient
                authStore.setPortalViewingPatientId(patient.id);
            } else if (!patient?.id) {
                // For 'self' application, ensure patient exists
                isLoading.value = false;
                return;
            }

            // Fetch the form packet (List resource) to get the forms
            const packetResponse = await $httpFhirApi.get(`/List/${portalPacketId}`);
            const formPacket = packetResponse.data;

            if (!formPacket) {
                console.error('No forms found in the portal packet');
                isLoading.value = false;
                return;
            }

            // Get the patient reference (either current user's patient or newly created one)
            const patientRef = { id: patient.id, resourceType: 'Patient' };

            // Create encounter directly without requiring a program
            const encounterResource = new Encounter({
                id: uuidv4(),
                status: 'in-progress',
                class: { code: 'AMB', display: 'ambulatory' },
                type: [{ coding: [{ code: 'portalPacket', display: 'Portal packet' }] }],
                subject: patientRef,
                period: { start: new Date().toISOString() },
                location: [{ location: { display: 'Unknown' } }],
            });

            // Only create the encounter - addForm will create the necessary tasks
            const transaction = [crud.post(encounterResource)];

            if (applicationType === 'self') {
                patient.inquiryStatus = 'pending';
                transaction.push(crud.patch(patient));
            }
            const response = await postTransaction(transaction);

            const encounter = response?.find((item) => item.resourceType === 'Encounter');
            if (!encounter) {
                console.error('Failed to create portal packet encounter');
                isLoading.value = false;
                return;
            }

            // Add forms from the packet to the encounter
            const { addForm } = useEncounterFlow();
            // Managed forms this tenant inactivated for its own system must be skipped (compared on base urls).
            const suppressedCatalogUrls = await useProfileFlow().getSuppressedCatalogUrls();
            let formTasks = null;
            for (const entry of formPacket.entry || []) {
                const formUrl = entry.item?.uri;
                if (!formUrl) continue;
                if (suppressedCatalogUrls.has(getUrlFromCanonical(formUrl).url)) continue;

                const workflowData = {
                    encounter: encounter,
                    form: formUrl,
                    filler: {
                        id: authStore.patient?.id,
                        resourceType: authStore.patient?.resourceType,
                    },
                    isPortal: true,
                };

                const formResponse = await addForm(workflowData);
                if (!formTasks) {
                    formTasks = formResponse;
                }
            }

            // Find the main task and open the form filler
            const mainTask = formTasks?.find((resource) => resource.resourceType === 'Task' && !resource.partOf?.length);
            if (mainTask) {
                router.push({
                    name: route.name || 'dashboard',
                    params: { ...route.params, activeDialog: 'form', activeId: mainTask.id },
                });
            }
        } catch (error) {
            console.error('Error starting application:', error);
        } finally {
            isLoading.value = false;
        }
    };

    /**
     * Convenience method to start a new application for someone else
     */
    const startNewApplication = async () => {
        await startApplication('other');
    };

    return {
        isLoading,
        startApplication,
        startNewApplication,
    };
}
